import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchCheckoutSessionStatus, invalidateAuthorizedExamsCache } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

function clearCheckoutQuery() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
}

export default function CheckoutReturnScreen({ status, sessionId, onDone, onNeedLogin }) {
  const { t } = useTranslation();
  const { isAuthenticated, isGuest, loading: authLoading } = useAuth();
  const [message, setMessage] = useState(
    status === 'cancel' ? t('billing.cancel_body') : t('billing.success_pending')
  );
  const [busy, setBusy] = useState(status === 'success');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (status === 'cancel') {
      setBusy(false);
      return undefined;
    }
    if (authLoading) return undefined;
    if (!isAuthenticated || isGuest) {
      setBusy(false);
      setMessage(t('billing.sign_in_required'));
      return undefined;
    }
    if (!sessionId) {
      setBusy(false);
      setMessage(t('billing.success_ready'));
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;

    const poll = async () => {
      attempts += 1;
      try {
        const data = await fetchCheckoutSessionStatus(sessionId);
        if (cancelled) return;
        if (data.fulfilled || data.paymentStatus === 'paid') {
          invalidateAuthorizedExamsCache();
          setBusy(false);
          setMessage(t('billing.success_ready'));
          return;
        }
        if (attempts >= maxAttempts) {
          setBusy(false);
          setMessage(t('billing.success_pending_slow'));
          return;
        }
        window.setTimeout(poll, 1500);
      } catch (e) {
        if (cancelled) return;
        setBusy(false);
        setError(e?.response?.data?.message || e.message || t('billing.lookup_error'));
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [status, sessionId, authLoading, isAuthenticated, isGuest, t]);

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: '#e8edf3',
        color: '#0f172a',
      }}
    >
      <div style={{ maxWidth: 560, margin: '48px auto', padding: '0 16px' }}>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 14,
            padding: '28px 24px',
            border: '1px solid #dce3ec',
            boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
          }}
        >
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>
            {status === 'cancel' ? t('billing.cancel_title') : t('billing.success_title')}
          </h1>
          {busy && (
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner size="large" />
            </div>
          )}
          <p style={{ marginTop: 16, fontSize: 15, lineHeight: 1.55, color: '#334155' }}>{message}</p>
          {error && (
            <p style={{ marginTop: 12, color: '#b91c1c', fontSize: 14 }} role="alert">
              {error}
            </p>
          )}
          <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {(!isAuthenticated || isGuest) && onNeedLogin && (
              <button
                type="button"
                onClick={onNeedLogin}
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: 10,
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {t('userMenu.signIn')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                clearCheckoutQuery();
                if (onDone) onDone();
              }}
              style={{
                padding: '10px 18px',
                fontSize: 14,
                fontWeight: 700,
                border: '1px solid #cbd5e1',
                borderRadius: 10,
                backgroundColor: '#fff',
                color: '#0f172a',
                cursor: 'pointer',
              }}
            >
              {t('billing.back_to_exams')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
